import React, { useState, useEffect, useCallback, useRef } from "react";
import { Input, Button, Avatar, Tabs, Col, Row } from "antd";
import {
  SendOutlined,
  SearchOutlined,
  FilterOutlined,
  PaperClipOutlined,
  PictureOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import "./chat.scss";
import ChatSidebar from "./ChatSidebar";
import ChatHeader from "./ChatHeader";
import ChatScreen from "./ChatScreen";
import API_MANAGER from "../../API";
import { jobStatusEnum } from "../../utils";
import { leaveRoom, socket } from "../../components/Socket/socket";
import { getMessagePreviewText } from "./messageDisplay";

const { TabPane } = Tabs;

const getMemberDisplayName = (m) => {
  if (!m) return "";
  return (
    m?.candidate?.name ||
    m?.name ||
    `${m?.firstName || ""} ${m?.lastName || ""}`.trim() ||
    m?.email ||
    ""
  );
};

const isNamedMember = (m) => {
  const n = (getMemberDisplayName(m) || "").trim();
  return n.length > 0 && n.toLowerCase() !== "unknown";
};

const countNamedMembers = (members) => {
  if (!Array.isArray(members)) return 0;
  return members.filter(isNamedMember).length;
};

const toGlobalSearchRoom = (room, isArchive = false) => {
  const roomId = room._id || room.roomId || room.id;
  if (!roomId) return null;

  const rawCampaign = room.advertId?.campaignId;
  const campaignName =
    (Array.isArray(rawCampaign) ? rawCampaign[0]?.name : rawCampaign?.name) ||
    room.campaignName ||
    "";
  const advertName = room.advertId?.jobTitle?.name || room.advertName || "";

  return {
    _id: roomId,
    _resolvedRoomId: roomId,
    type: room.type,
    members: room.members || [],
    campaignName,
    advertName,
    advertId: room.advertId,
    title: room.title,
    name: room.name,
    isArchive: isArchive || Boolean(room.isArchive),
  };
};

const buildChatFromSearchRoom = (room) => {
  const roomId = room._id || room.roomId || room.id;
  if (!roomId) return null;

  const currentUserId = localStorage.getItem("userId") || localStorage.getItem("userID");
  const isGroupChat = room.type === "Group" || room.type === "group";
  const members = room.members || [];
  const otherMember = members.find(
    (member) => String(member._id || member.id) !== String(currentUserId)
  );

  let name = "";
  if (isGroupChat) {
    name = room.title || room.name || "Group Chat";
  } else {
    const rawName =
      otherMember?.candidate?.name ||
      otherMember?.name ||
      `${otherMember?.firstName || ""} ${otherMember?.lastName || ""}`.trim() ||
      otherMember?.email ||
      "Unknown";
    name = rawName.trim() || "Unknown";
  }

  const campaignName = room.campaignName || "";
  const advertName = room.advertName || "";
  let role = "";
  if (campaignName && advertName) role = `${campaignName} - ${advertName}`;
  else role = campaignName || advertName || "";

  return {
    id: roomId,
    roomId,
    name,
    role,
    location: "",
    message: "",
    time: "",
    unreadCount: 0,
    avatarUrl: null,
    statusLabel: "",
    isGroup: isGroupChat,
    members,
    isArchive: Boolean(room.isArchive),
  };
};

const Chat = ({ showArchived = false, setShowArchived = () => {} }) => {
  const location = useLocation();
  const [selectedChat, setSelectedChat] = useState(null);
  const [messageInput, setMessageInput] = useState("");
  const [chatRoomsResponse,setChatRoomsResponse] = useState([])
  const [chats, setChats] = useState([]);
  const [allChats, setAllChats] = useState([]);
  const allChatsRef = useRef(allChats);
  useEffect(() => {
    allChatsRef.current = allChats;
  }, [allChats]);
  const [loadingChats, setLoadingChats] = useState(false);
  const [hasMoreChats, setHasMoreChats] = useState(true);
  const [chatPage, setChatPage] = useState(1);
  const fetchingChatRoomsRef = useRef(false);
  const [containerHeight, setContainerHeight] = useState("75vh");
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  const selectedChatRef = useRef(null);

  useEffect(() => {
    const updateHeight = () => {
      if (window.innerWidth >= 768) {
        setContainerHeight("calc(100vh - 200px)");
      } else {
        setContainerHeight("75vh");
      }
      setIsMobile(window.innerWidth <= 768);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  // Keep ref in sync with selectedChat state
  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  // Leave room when Chat component unmounts (user leaves chat section)
  useEffect(() => {
    return () => {
      const storedRoomId = sessionStorage.getItem("leaveroom");
      if (storedRoomId) {
        leaveRoom(storedRoomId);
        sessionStorage.removeItem("leaveroom");
      }
    };
  }, []);

  // Handler to clear unread count when chat is selected
  const handleChatSelect = useCallback((chat) => {
    if (chat) {
      const { _searchMessageId, ...cleanChat } = chat;
      setScrollToMessageId(_searchMessageId || null);
      setAllChats((prev) =>
        prev.map((c) =>
          c.id === chat.id || c.roomId === chat.id || c.roomId === chat.roomId
            ? { ...c, unreadCount: 0 }
            : c
        )
      );
      setChats((prev) =>
        prev.map((c) =>
          c.id === chat.id || c.roomId === chat.id || c.roomId === chat.roomId
            ? { ...c, unreadCount: 0 }
            : c
        )
      );
      setSelectedChat(cleanChat);
      try {
        const persistId = chat.roomId || chat.id;
        if (persistId) localStorage.setItem("lastSelectedChatId", String(persistId));
      } catch {}
    } else {
      setScrollToMessageId(null);
      setSelectedChat(null);
      try {
        localStorage.removeItem("lastSelectedChatId");
      } catch {}
    }
  }, []);

  const hasRestoredLastChatRef = useRef(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchMode, setSearchMode] = useState(() => {
    return localStorage.getItem("chatSearchMode") || "candidate";
  });
  const [selectedAdverts, setSelectedAdverts] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [globalSearchResults, setGlobalSearchResults] = useState({ rooms: [], messages: [] });
  const [globalSearchLoading, setGlobalSearchLoading] = useState(false);
  const globalSearchTimerRef = useRef(null);
  const globalSearchRequestIdRef = useRef(0);
  const searchRoomCacheRef = useRef(new Map());
  const [scrollToMessageId, setScrollToMessageId] = useState(null);


  const getProfileImageUrl = (profileImage) => {
    if (!profileImage) return null;
    if (profileImage.startsWith("http")) return profileImage;
    return `https://dev-documents-cinchy.s3.ap-southeast-2.amazonaws.com/${profileImage}`;
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInMins = Math.floor(diffInMs / 60000);
    const diffInHours = Math.floor(diffInMs / 3600000);
    const diffInDays = Math.floor(diffInMs / 86400000);

    if (diffInMins < 1) return "Just now";
    if (diffInMins < 60) return `${diffInMins}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  const fetchChatRooms = useCallback(async (archive = false, page = 1, limit = 20, append = false) => {
      // Guard to prevent concurrent calls (only for non-append calls)
      if (!append && fetchingChatRoomsRef.current) {
        return { hasMore: false, totalRecords: 0 };
      }
      
      if (!append) {
        fetchingChatRoomsRef.current = true;
        setLoadingChats(true);
      }
      
      try {
        const response = await API_MANAGER.getChatRooms(archive, { page, limit });
        const records = response?.data?.data?.[0]?.records || response?.data?.records || [];
        const totalRecords = response?.data?.data?.[0]?.totalRecords?.[0]?.total || response?.data?.totalRecords?.[0]?.total || response?.data?.total || records.length;
        const currentUserId = localStorage.getItem("userId") || localStorage.getItem("userID");
  
        // Build chat list from the getChatRooms payload only. We intentionally
        // do NOT fire a per-room getChatMessages call here: the last message is
        // taken from the room payload, and full messages are loaded lazily when
        // the user opens a chat. This avoids dozens of sequential API calls that
        // were slowing down the initial render / search.
        const chatsWithMessages = records.map((room) => {
            // Check if this is a group chat
            const isGroupChat = room.type === "Group" || room.type === "group";

            const otherMember = room.members?.find(
              (member) => member._id !== currentUserId
            );
  
            // For private chats, otherMember is required; for group chats it's optional
            if (!isGroupChat && !otherMember) return null;

            let name, profileImage, role, location;

            if (isGroupChat) {
              name = room.title || room.name || "Group Chat";
              profileImage = room.logo || null;
              const memberCount = countNamedMembers(room.members);
              const jobTitle = room.advertId?.jobTitle?.name || "";
              const rawCampaignId = room.advertId?.campaignId;
              const campaignName = (Array.isArray(rawCampaignId) ? rawCampaignId[0]?.name : rawCampaignId?.name) || "";
              if (campaignName && jobTitle) {
                role = `${campaignName} - ${jobTitle}`;
              } else if (campaignName) {
                role = campaignName;
              } else if (jobTitle) {
                role = jobTitle;
              } else {
                role = "";
              }
              const candidateMember = room.members?.find(
                (member) => member._id !== currentUserId && member.candidate
              );
              const gc = candidateMember?.candidate;
              if (gc) {
                const cParts = [];
                if (gc.city) cParts.push(gc.city);
                if (gc.state && typeof gc.state === "object" && gc.state.name) cParts.push(gc.state.name);
                else if (gc.state && typeof gc.state === "string") cParts.push(gc.state);
                if (gc.postalCode) cParts.push(gc.postalCode);
                location = cParts.join(", ");
              } else {
                const rawCampaign = room.advertId?.campaignId;
                const groupCampaign = Array.isArray(rawCampaign) ? rawCampaign[0] : rawCampaign;
                const locParts = [];
                if (groupCampaign?.suburb) locParts.push(groupCampaign.suburb);
                if (groupCampaign?.state?.name) locParts.push(groupCampaign.state.name);
                else if (groupCampaign?.state && typeof groupCampaign.state === "string") locParts.push(groupCampaign.state);
                if (groupCampaign?.postalCode) locParts.push(groupCampaign.postalCode);
                location = locParts.join(", ");
              }
            } else {
              // Private chat: use other member's info
              const candidate = otherMember.candidate;
              const rawName = candidate?.name || `${otherMember.firstName || ""} ${otherMember.lastName || ""}`.trim() || otherMember.email || "Unknown";
              name = rawName.trim() || "Unknown";
              profileImage = candidate?.profileImage || null;
              const jobTitle = room.advertId?.jobTitle?.name || "";
              const rawPrivateCampaign = room.advertId?.campaignId;
              const campaignName = (Array.isArray(rawPrivateCampaign) ? rawPrivateCampaign[0]?.name : rawPrivateCampaign?.name) || "";
              role = "";
              if (campaignName && jobTitle) {
                role = `${campaignName} - ${jobTitle}`;
              } else if (campaignName) {
                role = campaignName;
              } else if (jobTitle) {
                role = jobTitle;
              }
              if (candidate) {
                const pParts = [];
                if (candidate.city) pParts.push(candidate.city);
                if (candidate.state && typeof candidate.state === "object" && candidate.state.name) pParts.push(candidate.state.name);
                else if (candidate.state && typeof candidate.state === "string") pParts.push(candidate.state);
                if (candidate.postalCode) pParts.push(candidate.postalCode);
                location = pParts.join(", ");
              } else {
                location = "";
              }
            }
  
          // Get last message (regardless of sender). The rooms API returns the
          // most recent message as `latestMessage` with `sortDate` as its
          // timestamp; `lastMessage`/`messages` are kept as fallbacks.
          let lastMessage = "";
          let lastMessageTime = room.sortDate || room.updatedAt;

          if (room.latestMessage && typeof room.latestMessage === "object") {
            lastMessage = getMessagePreviewText(room.latestMessage, {
              members: room.members || [],
            });
            lastMessageTime =
              room.latestMessage.createdAt ||
              room.latestMessage.updatedAt ||
              room.sortDate ||
              room.updatedAt;
          }

          if (!lastMessage && room.lastMessage) {
            if (typeof room.lastMessage === "object") {
              lastMessage = getMessagePreviewText(room.lastMessage, {
                members: room.members || [],
              });
              lastMessageTime = room.lastMessage.createdAt || room.lastMessage.updatedAt || room.lastMessage.timestamp || lastMessageTime;
            } else if (typeof room.lastMessage === "string") {
              lastMessage = room.lastMessage;
            }
          }
          
          // Check messages array - get last message (first in array if reverse sorted, or last if normal sorted)
          if (!lastMessage && room.messages && Array.isArray(room.messages) && room.messages.length > 0) {
            // Get the last message from the array (assuming it's sorted by time)
            const lastMsg = room.messages[room.messages.length - 1];
            
            if (lastMsg) {
              lastMessage = getMessagePreviewText(lastMsg, {
                members: room.members || [],
              });
              // Get timestamp from last message
              lastMessageTime = lastMsg.createdAt || lastMsg.updatedAt || lastMsg.timestamp || lastMessageTime;
            }
          }
          
            // Get application status from otherMember.application (from getChatRooms API response)
            let statusLabel = "";
            let applicationState = -1;
            
            if (isGroupChat) {
              statusLabel = "INVITED";
              const candidateMemberForStatus = room.members?.find(
                (member) => member._id !== currentUserId && member.application
              );
              if (candidateMemberForStatus?.application) {
                const application = candidateMemberForStatus.application;
                if (application.progress && Array.isArray(application.progress) && application.progress.length > 0) {
                  const states = application.progress
                    .map(p => p?.state)
                    .filter(state => state !== null && state !== undefined && state !== -1 && typeof state === 'number');
                  if (states.length > 0) {
                    applicationState = Math.max(...states);
                  }
                }
              }
              if (applicationState !== -1 && applicationState !== undefined && applicationState >= 0 && applicationState <= 5) {
                const applicantStatus = jobStatusEnum[applicationState];
                statusLabel = applicantStatus?.label || "INVITED";
              }
            } else if (String(room.type || "").toLowerCase() === "direct") {
              // Profile-started chats use type "Direct" from get-rooms — show DIRECT, not INVITED
              statusLabel = "DIRECT";
            } else {
              statusLabel = "INVITED";
              if (otherMember?.application && otherMember.application !== null) {
                const application = otherMember.application;
                
                if (application.progress && Array.isArray(application.progress) && application.progress.length > 0) {
                  const states = application.progress
                    .map(p => p?.state)
                    .filter(state => state !== null && state !== undefined && state !== -1 && typeof state === 'number');
                  
                  if (states.length > 0) {
                    applicationState = Math.max(...states);
                  }
                }
              }
              
              if (applicationState !== -1 && applicationState !== undefined && applicationState >= 0 && applicationState <= 5) {
                const applicantStatus = jobStatusEnum[applicationState];
                statusLabel = applicantStatus?.label || "INVITED";
              }
            }

            return {
              id: room._id,
              name: name,
              role: role,
              location: location,
              message: lastMessage,
              time: formatTimeAgo(lastMessageTime),
              lastMessageTime: lastMessageTime,
              updatedAt: room.updatedAt,
              unreadCount: typeof room.unreadCount === 'number' ? room.unreadCount : (room.unreadCount ? parseInt(room.unreadCount) || 0 : 0),
              avatarUrl: getProfileImageUrl(profileImage),
              roomId: room._id,
              otherMemberId: otherMember?._id || null,
              advertId: room.advertId?._id,
              campaignId: (Array.isArray(room.advertId?.campaignId)
                ? room.advertId?.campaignId?.[0]?._id
                : room.advertId?.campaignId?._id) || null,
              statusLabel: statusLabel,
              type: room.type || (isGroupChat ? "Group" : "Private"),
              isGroup: isGroupChat,
              memberCount: isGroupChat ? countNamedMembers(room.members) : undefined,
              members: room.members || [],
            };
          });

        const transformedChats = chatsWithMessages.filter(Boolean);
  
        if (append) {
          const mergeUnique = (prev) => {
            const seen = new Set(
              prev.map((c) => c.id || c.roomId || c._id).filter(Boolean)
            );
            const next = transformedChats.filter((c) => {
              const id = c.id || c.roomId || c._id;
              if (!id || seen.has(id)) return false;
              seen.add(id);
              return true;
            });
            return [...prev, ...next];
          };
          setAllChats(mergeUnique);
          setChats(mergeUnique);
        } else {
          setAllChats(transformedChats);
          setChats(transformedChats);
        }
        
        // Build campaign map for filters (only on first page load)
        if (!append) {
          const campaignMap = new Map();
        records.forEach((room) => {
          const campaignId = room.advertId?.campaignId?.[0]?._id;
          const campaignName = room.advertId?.campaignId?.[0]?.name;
          const advertId = room.advertId?._id;
          const jobTitle = room.advertId?.jobTitle?.name;
          
          if (campaignId && advertId) {
            if (!campaignMap.has(campaignId)) {
              campaignMap.set(campaignId, {
                _id: campaignId,
                name: campaignName,
                adverts: [],
              });
            }
            const campaign = campaignMap.get(campaignId);
            if (!campaign.adverts.find(a => a._id === advertId)) {
              campaign.adverts.push({
                _id: advertId,
                jobTitle: { name: jobTitle },
              });
            }
          }
        });
        
          setFilterData(Array.from(campaignMap.values()));
        }
        
        setChatRoomsResponse(response);
        setLoadingChats(false);
        fetchingChatRoomsRef.current = false;
        
        return {
          hasMore: transformedChats.length >= limit && transformedChats.length < totalRecords,
          totalRecords: totalRecords
        };
      } catch (error) {
        console.error("Failed to fetch chat rooms:", error);
        setLoadingChats(false);
        fetchingChatRoomsRef.current = false;
        return { hasMore: false, totalRecords: 0 };
      }
    }, [formatTimeAgo, getProfileImageUrl]);

  // Track if we've loaded initial data for current showArchived state
  const initialLoadRef = useRef({ loaded: false, archiveState: null });
  
  // Initial load and when showArchived changes
  useEffect(() => {
    // Check if we need to load (either first time or archive state changed)
    const needsLoad = !initialLoadRef.current.loaded || initialLoadRef.current.archiveState !== showArchived;
    
    if (needsLoad) {
      // Reset ref and pagination state
      fetchingChatRoomsRef.current = false;
      setChatPage(1);
      setHasMoreChats(true);
      setChats([]);
      setAllChats([]);
      
      // Mark as loaded for this archive state
      initialLoadRef.current.loaded = true;
      initialLoadRef.current.archiveState = showArchived;
      
      // Fetch chat rooms on initial load and when showArchived changes
      fetchChatRooms(showArchived, 1, 20, false).then((result) => {
        if (result) {
          setHasMoreChats(result.hasMore);
        } else {
          setHasMoreChats(false);
        }
      }).catch((error) => {
        console.error("Error fetching chat rooms:", error);
        setHasMoreChats(false);
      });
    }
  }, [showArchived]); // Only depend on showArchived, not fetchChatRooms

  // Real-time socket listener for new messages to update chat list
  // This listener tracks ALL messages from ALL chats, regardless of selection
  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (newMessage) => {
      const messageRoomId = newMessage.roomId || newMessage.room?._id;
      if (!messageRoomId) return;

      const currentUserId = localStorage.getItem("userId") || localStorage.getItem("userID");
      
      // Get sender ID from message
      const messageSenderId = newMessage.senderId?._id || newMessage.senderId?.id || newMessage.senderId;
      const isFromCurrentUser = String(messageSenderId) === String(currentUserId);
      
      // Get selected chat ID for comparison using ref (avoids dependency issues)
      const currentSelectedChat = selectedChatRef.current;
      const selectedChatId = currentSelectedChat?.id || currentSelectedChat?.roomId;
      const isSelectedChat = selectedChatId === messageRoomId || 
                            currentSelectedChat?.roomId === messageRoomId ||
                            currentSelectedChat?.id === messageRoomId;

      // Update allChats
      setAllChats((prevChats) => {
        const chatIndex = prevChats.findIndex(
          (chat) => chat.roomId === messageRoomId || chat.id === messageRoomId
        );

        if (chatIndex === -1) {
          // Chat not found, might be a new chat - refresh the list (only if not already fetching)
          if (!fetchingChatRoomsRef.current) {
            fetchChatRooms(showArchived, 1, 20, false);
          }
          return prevChats;
        }

        const updatedChats = [...prevChats];
        const chat = updatedChats[chatIndex];
        
        // Update message preview
        let messagePreview = getMessagePreviewText(newMessage, {
          members: chat.members || [],
        });

        // Get timestamp
        const messageTime = newMessage.createdAt || newMessage.updatedAt || new Date().toISOString();
        
        // Calculate unread count:
        // - Don't increment if message is from current user
        // - Don't increment if this chat is currently selected
        // - Otherwise, increment the unread count
        let newUnreadCount = chat.unreadCount || 0;
        if (!isFromCurrentUser && !isSelectedChat) {
          newUnreadCount = newUnreadCount + 1;
        }
        
        // Update chat with new message info
        updatedChats[chatIndex] = {
          ...chat,
          message: messagePreview,
          time: formatTimeAgo(messageTime),
          lastMessageTime: messageTime,
          updatedAt: messageTime,
          unreadCount: newUnreadCount,
        };

        return updatedChats;
      });

      // Also update the filtered chats
      setChats((prevChats) => {
        const chatIndex = prevChats.findIndex(
          (chat) => chat.roomId === messageRoomId || chat.id === messageRoomId
        );

        if (chatIndex === -1) {
          return prevChats;
        }

        const updatedChats = [...prevChats];
        const chat = updatedChats[chatIndex];
        
        let messagePreview = getMessagePreviewText(newMessage, {
          members: chat.members || [],
        });

        const messageTime = newMessage.createdAt || newMessage.updatedAt || new Date().toISOString();
        
        // Calculate unread count (same logic as above)
        let newUnreadCount = chat.unreadCount || 0;
        if (!isFromCurrentUser && !isSelectedChat) {
          newUnreadCount = newUnreadCount + 1;
        }
        
        updatedChats[chatIndex] = {
          ...chat,
          message: messagePreview,
          time: formatTimeAgo(messageTime),
          lastMessageTime: messageTime,
          updatedAt: messageTime,
          unreadCount: newUnreadCount,
        };

        return updatedChats;
      });
    };

    socket.on("new-message", handleNewMessage);

    return () => {
      socket.off("new-message", handleNewMessage);
    };
  }, [socket, showArchived, fetchChatRooms]);

  // Restore chat selection when returning from applicant profile
  useEffect(() => {
    if (location?.state?.fromChat && location?.state?.selectedChat) {
      const chatToRestore = location.state.selectedChat;
      // Find the chat in the current chats list
      const foundChat = chats.find(c => c.id === chatToRestore.id || c.roomId === chatToRestore.id);
      if (foundChat) {
        setSelectedChat(foundChat);
        handleChatSelect(foundChat);
      } else {
        // If chat not found, wait for chats to load and try again
        const checkAndRestore = () => {
          const found = chats.find(c => c.id === chatToRestore.id || c.roomId === chatToRestore.id);
          if (found) {
            setSelectedChat(found);
            handleChatSelect(found);
          }
        };
        // Try after a short delay to allow chats to load
        setTimeout(checkAndRestore, 500);
      }
    }
  }, [location?.state, chats]);

  // Restore last selected chat on page load / tab switch from localStorage
  useEffect(() => {
    if (hasRestoredLastChatRef.current) return;
    if (selectedChat) return;
    if (!Array.isArray(chats) || chats.length === 0) return;
    if (location?.state?.fromChat && location?.state?.selectedChat) return;

    try {
      const lastId = localStorage.getItem("lastSelectedChatId");
      if (!lastId) {
        hasRestoredLastChatRef.current = true;
        return;
      }
      const found = chats.find(
        (c) => String(c.id) === String(lastId) || String(c.roomId) === String(lastId)
      );
      if (found) {
        hasRestoredLastChatRef.current = true;
        handleChatSelect(found);
      }
    } catch {}
  }, [chats, selectedChat, location?.state, handleChatSelect]);

  const resolveMissingSearchRooms = async (missingRoomIds) => {
    const resolved = [];
    const stillMissing = missingRoomIds.filter((id) => {
      const cached = searchRoomCacheRef.current.get(id);
      if (cached) {
        resolved.push(cached);
        return false;
      }
      return true;
    });
    if (!stillMissing.length) return resolved;

    const found = new Map();
    for (const archive of [true, false]) {
      if (found.size >= stillMissing.length) break;
      let page = 1;
      let hasMore = true;
      while (hasMore && found.size < stillMissing.length && page <= 2) {
        try {
          const response = await API_MANAGER.getChatRooms(archive, { page, limit: 50 });
          const records =
            response?.data?.data?.[0]?.records || response?.data?.records || [];
          records.forEach((room) => {
            const id = room._id;
            if (id && stillMissing.includes(id) && !found.has(id)) {
              found.set(id, toGlobalSearchRoom(room, archive));
            }
          });
          const total =
            response?.data?.data?.[0]?.totalRecords?.[0]?.total ||
            response?.data?.totalRecords?.[0]?.total ||
            records.length;
          hasMore = records.length > 0 && page * 50 < total;
          page += 1;
        } catch {
          break;
        }
      }
    }

    stillMissing.forEach((id) => {
      const room = found.get(id);
      if (room) {
        searchRoomCacheRef.current.set(id, room);
        resolved.push(room);
      }
    });
    return resolved;
  };

  useEffect(() => {
    const query = searchQuery.trim();
    if (!query) {
      globalSearchRequestIdRef.current += 1;
      setGlobalSearchResults({ rooms: [], messages: [] });
      setGlobalSearchLoading(false);
      if (globalSearchTimerRef.current) clearTimeout(globalSearchTimerRef.current);
      return;
    }

    // Keep the previous results visible while debouncing/fetching so the list
    // narrows smoothly instead of clearing and re-rendering (which caused flicker).
    setGlobalSearchLoading(true);
    const requestId = ++globalSearchRequestIdRef.current;

    if (globalSearchTimerRef.current) clearTimeout(globalSearchTimerRef.current);
    globalSearchTimerRef.current = setTimeout(async () => {
      try {
        const res = await API_MANAGER.globalChatSearch(query);
        // Ignore responses that are no longer for the latest query.
        if (requestId !== globalSearchRequestIdRef.current) return;
        const payload = res?.data?.data ?? res?.data ?? {};

        const rooms = Array.isArray(payload?.rooms) ? payload.rooms : [];
        const messages = Array.isArray(payload?.messages) ? payload.messages : [];

        const roomIdentityKey = (item) => {
          const type = (item.type || "").toLowerCase();
          const campaign = item.campaignId || item.campaignName || "";
          const advert = item.advertName || item.advertId?.jobTitle?.name || "";
          const title = (item.title || "").toLowerCase();
          const emails = Array.isArray(item.members)
            ? item.members
                .map((m) => (m.email || m._id || "").toLowerCase())
                .filter(Boolean)
                .sort()
                .join(",")
            : "";
          return `${type}|${campaign}|${advert}|${title}|${emails}`;
        };

        const roomMap = new Map();
        rooms.forEach((item) => {
          const roomId = item._id || item.roomId || item.id;
          if (!roomId) return;
          roomMap.set(roomIdentityKey(item), { ...item, _resolvedRoomId: roomId });
        });
        const dedupRooms = Array.from(roomMap.values());

        const messageRoomIds = [
          ...new Set(messages.map((item) => item.roomId).filter(Boolean)),
        ];
        const knownRoomIds = new Set([
          ...dedupRooms.map((room) => room._resolvedRoomId || room._id),
          ...allChatsRef.current.map((chat) => chat.id || chat.roomId).filter(Boolean),
        ]);
        const missingRoomIds = messageRoomIds.filter((id) => !knownRoomIds.has(id));
        const extraRooms =
          missingRoomIds.length > 0
            ? await resolveMissingSearchRooms(missingRoomIds)
            : [];

        if (requestId !== globalSearchRequestIdRef.current) return;

        setGlobalSearchResults({
          rooms: [...dedupRooms, ...extraRooms],
          messages,
        });
      } catch {
        if (requestId !== globalSearchRequestIdRef.current) return;
        setGlobalSearchResults({ rooms: [], messages: [] });
      } finally {
        if (requestId === globalSearchRequestIdRef.current) {
          setGlobalSearchLoading(false);
        }
      }
    }, 400);
    return () => {
      if (globalSearchTimerRef.current) clearTimeout(globalSearchTimerRef.current);
    };
  }, [searchQuery, searchMode]);

  useEffect(() => {
    let filtered = [...allChats];

    if (searchQuery.trim() && searchMode === "candidate") {
      const apiRooms = globalSearchResults.rooms || [];

      // Only render results that resolve to a fully-loaded chat so cards always
      // appear complete. Rooms not yet in allChats are omitted until the
      // background auto-load brings them in (then they render fully-formed),
      // which avoids the "small email-only card that later expands" flicker.
      const apiMatches = apiRooms
        .map((room) => {
          const existing = allChats.find(
            (c) =>
              c.id === room._id ||
              c.roomId === room._id ||
              c._id === room._id
          );
          if (existing) {
            return { ...existing, isArchive: Boolean(room.isArchive ?? existing.isArchive) };
          }
          return buildChatFromSearchRoom(room);
        })
        .filter(Boolean);
      
      const seenIds = new Set();
      filtered = apiMatches.filter((chat) => {
        const id = chat.id || chat.roomId;
        if (!id || seenIds.has(id)) return false;
        seenIds.add(id);
        return true;
      });

      // While the debounced search is still in flight and we don't yet have any
      // resolved matches for the current query, keep the currently displayed
      // list instead of clearing it to empty. This lets the list narrow
      // smoothly once results arrive rather than flashing empty/spinner on
      // every keystroke.
      if (globalSearchLoading && filtered.length === 0) {
        return;
      }
    }

    if (selectedAdverts.length > 0) {
      filtered = filtered.filter((chat) =>
        selectedAdverts.includes(chat.advertId)
      );
    }

    setChats(filtered);
  }, [searchQuery, searchMode, allChats, selectedAdverts, globalSearchResults, globalSearchLoading]);
  return (
   <div style={{ height: "100%", overflow: "hidden", display: "flex", flexDirection: "column" }}>
  <ChatHeader 
    searchQuery={searchQuery} 
    onSearchChange={(val) => { setSearchQuery(val); }}
    searchMode={searchMode}
    onSearchModeChange={(mode) => { setSearchMode(mode); setSearchQuery(""); localStorage.setItem("chatSearchMode", mode); }}
    filterData={filterData}
    selectedAdverts={selectedAdverts}
    setSelectedAdverts={setSelectedAdverts}
    showArchived={showArchived}
    onArchiveClick={() => {
      const newArchiveState = !showArchived;
      setShowArchived(newArchiveState);
      fetchChatRooms(newArchiveState);
    }}
  />

  <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", flex: 1, marginTop:"32px",justifyContent:"space-between", gap: isMobile ? "16px" : 0, overflow: "hidden", minHeight: 0 }}>
    {/* Sidebar */}
    <div style={{ flex: isMobile ? "1 1 auto" : "0 0 35%", minWidth: 0, width: isMobile ? "100%" : "auto", display: isMobile && selectedChat ? "none" : "block", overflow: "hidden", border: "1px solid #E4E8ED",borderRadius:"12px" }}>
      <ChatSidebar chatRoomsResponse ={chatRoomsResponse} chats={chats} setChats={setChats} loadingChats={loadingChats}
      onChatSelect={handleChatSelect} selectedChat={selectedChat} fetchChatRooms={fetchChatRooms}
      formatTimeAgo={formatTimeAgo}
      getProfileImageUrl={getProfileImageUrl} showArchived={showArchived} 
      allChats={allChats} setAllChats={setAllChats}
      globalSearchResults={globalSearchResults}
      globalSearchLoading={globalSearchLoading}
      isGlobalSearch={searchMode === "chat" && searchQuery.trim().length > 0}
      globalSearchQuery={searchQuery}
      onArchiveClick={() => {
        const newArchiveState = !showArchived;
        setShowArchived(newArchiveState);
        fetchChatRooms(newArchiveState);
      }} />
    </div>

    {/* Chat Screen */}
    <div style={{ flex: isMobile ? "1 1 auto" : "0 0 64%", minWidth: 0, width: isMobile ? "100%" : "auto", display: isMobile && !selectedChat ? "none" : "flex", flexDirection: "column", overflow: "hidden", border: "1px solid #E4E8ED",borderRadius:"12px" }}>
      {isMobile && selectedChat && (
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => setSelectedChat(null)}
          style={{ alignSelf: "flex-start", margin: "8px 0 0 8px", fontWeight: 600, color: "#091647" }}
        >
          Back to chats
        </Button>
      )}
      {showArchived && !selectedChat ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
            background: "#fff",
          }}
        >
          <img
            src="/messagechat.svg"
            alt="Message Chat"
            style={{
              width: "200px",
              height: "auto",
              marginBottom: "24px",
            }}
          />
          <p
            style={{
              color: "#302C48",
              textAlign: "center",
              fontFamily: "Arial",
              fontSize: "18px",
              fontStyle: "normal",
              fontWeight: 700,
              lineHeight: "12px",
              margin: 0,
            }}
          >
            Your Messages Will Appear Here
          </p>
        </div>
      ) : (
        <ChatScreen selectedChat={selectedChat} getProfileImageUrl={getProfileImageUrl} scrollToMessageId={scrollToMessageId} onScrollToMessageHandled={() => setScrollToMessageId(null)} />
      )}
    </div>
  </div>
  </div>
  );
};

export default Chat;
